import { useDocumentVisibility } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { IReminderSummary } from '@/types/Reminder'

/*
 * How many opomniki are overdue and how many are coming up — one pair of numbers for
 * the whole console, so the menu badge is right without anybody opening the page.
 *
 * It is stores/alerts.ts with a slower clock and the same contract: it STARTS AT
 * NOTHING AND CAN ONLY ADD A BADGE, and every failure is swallowed — no toast, no
 * console line, no Sentry. A fleet-api deployed before this feature answers 404 to
 * /reminders/summary and the console then looks exactly as it did before this store
 * existed.
 *
 * Five minutes rather than one: what these two numbers depend on is a date and an
 * odometer, and the API itself only re-judges them once an hour. Polling faster would
 * be the same answer read again — and this runs beside the alerts' own poll on a
 * server that also carries four other stacks.
 */
const POLL_MS = 5 * 60_000

const emptySummary = (): IReminderSummary => ({ overdue: 0, due_soon: 0 })

export const useRemindersStore = defineStore('reminders', () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const visibility = useDocumentVisibility()

    const summary = ref<IReminderSummary>(emptySummary())

    let timer: ReturnType<typeof setInterval> | undefined

    /** What the badge counts, and in which colour: one overdue outranks ten coming up. */
    const badgeCount = computed<number>(() => summary.value.overdue || summary.value.due_soon)

    const badgeColour = computed<'error' | 'warning'>(() => summary.value.overdue > 0 ? 'error' : 'warning')

    const load = async () => {
        if (!appStore.isLoggedIn()) return

        const { statusCode, data } = await fetchStore.fetch('/reminders/summary').get().json<IReminderSummary>()

        if (statusCode.value !== 200 || !data.value) return

        summary.value = { ...emptySummary(), ...data.value }
    }

    const stopTimer = () => {
        if (timer) clearInterval(timer)

        timer = undefined
    }

    const start = () => {
        if (timer) return

        void load()

        timer = setInterval(() => {
            if (visibility.value === 'hidden') return

            void load()
        }, POLL_MS)
    }

    const reset = () => {
        stopTimer()
        summary.value = emptySummary()
    }

    /*
     * A tab that has been in the background half the morning is looking at a stale
     * number the moment somebody switches back to it, and — unlike the alerts — this
     * one is stale by five minutes rather than by one.
     */
    watch(visibility, (state) => {
        if (state === 'visible') void load()
    })

    /** Signing out throws the count away, like every other per-session state. */
    watch(() => appStore.JWT, (jwt) => {
        if (jwt) {
            start()
        } else {
            reset()
        }
    })

    return {
        summary,
        badgeCount, badgeColour,
        load, start, reset,
    }
})
